import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical, Shield, Truck, Loader2, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/products/ProductCard';
import { productsAPI, categoriesAPI, settingsAPI } from '../services/api';

const iconMap = {
  FlaskConical,
  Shield,
  Truck
};

const defaultFeatures = [
  { icon: 'FlaskConical', title: 'Premium Quality', description: 'Lab tested products' },
  { icon: 'Shield', title: 'Secure', description: 'Discreet packaging' },
  { icon: 'Truck', title: 'Fast Delivery', description: 'UK 1-2 days' }
];

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ features: defaultFeatures });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          productsAPI.getAll(),
          categoriesAPI.getAll(),
        ]);
        
        const allProducts = productsRes.data;
        setProducts(allProducts);
        
        // Get featured products (those with featured: true)
        const featured = allProducts.filter(p => p.featured);
        setFeaturedProducts(featured);
        
        setCategories(categoriesRes.data);

        // Fetch settings separately - don't block if it fails
        try {
          const settingsRes = await settingsAPI.get();
          setSettings({ ...settingsRes.data, features: defaultFeatures });
        } catch (e) {
          // Use defaults
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Show HOT/NEW products if no featured products
  const displayProducts = featuredProducts.length > 0 
    ? featuredProducts.slice(0, 8)
    : products.filter(p => p.badge === 'HOT' || p.badge === 'NEW').slice(0, 8);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 via-zinc-950 to-zinc-950" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.15)_0%,transparent_50%)]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-2 mb-8">
            <FlaskConical className="h-4 w-4 text-amber-500" />
            <span className="text-amber-500 font-medium">Premium Research Compounds</span>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black mb-6">
            <span className="text-white">GET </span>
            <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 bg-clip-text text-transparent">FORGED</span>
          </h1>

          {/* Subheading */}
          <p className="text-zinc-400 text-lg md:text-2xl mb-10 max-w-2xl mx-auto px-4">
            Premium research compounds for qualified researchers. For laboratory use only.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-14 px-8 text-lg">
                Shop Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="https://wa.me/447440352811" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800 h-14 px-8 text-lg">
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {settings.features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-zinc-900 rounded-xl p-6 border border-zinc-800"
                >
                  <div className="bg-amber-500/10 rounded-lg p-3">
                    <Icon className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{feature.title}</h3>
                    <p className="text-zinc-400 text-sm">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      {categories.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white">Shop by Category</h2>
                <p className="text-zinc-400 mt-2">Find exactly what you need</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/products?category=${category.slug}`}
                  className="group relative aspect-square rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 transition-all"
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-semibold text-sm md:text-base group-hover:text-amber-500 transition-colors">
                      {category.name}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products Section - Only show if there are products to display */}
      {displayProducts.length > 0 && (
        <section className="py-16 bg-zinc-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-3xl md:text-4xl font-bold text-white">
                    {featuredProducts.length > 0 ? 'Featured' : 'Popular Products'}
                  </h2>
                  {featuredProducts.length > 0 && (
                    <Star className="h-6 w-6 text-amber-500 fill-amber-500" />
                  )}
                </div>
                <p className="text-zinc-400 mt-2">
                  {featuredProducts.length > 0 ? 'Hand-picked products just for you' : 'Our most popular products'}
                </p>
              </div>
              <Link to="/products">
                <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">
                  View All
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-amber-600/20 to-amber-500/10 rounded-2xl p-8 md:p-12 text-center border border-amber-500/20">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Forged?
            </h2>
            <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
              Trusted by researchers for premium quality compounds. All products lab tested.
            </p>
            <Link to="/products">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-12 px-8 text-lg">
                Shop Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
